'use client';

import React from 'react';
import { cn } from '../lib/utils';
import { EmptyValue } from './empty-value';
import { CountBadge } from './count-badge';
import { StateKit } from './state-kit';

export interface WorkListField {
  id: string;
  label: string;
}
export interface WorkListRow {
  id: string;
  /** Human-readable identity used to name selection controls. */
  label: string;
  identity: React.ReactNode;
  state?: React.ReactNode;
  reason?: React.ReactNode;
  values?: Readonly<Record<string, React.ReactNode>>;
  /** Protected direct action. Never moved into the overflow slot. */
  action: React.ReactNode;
  overflow?: React.ReactNode;
  selectionDisabled?: boolean;
  dataAttributes?: Readonly<Record<`data-${string}`, string>>;
}
export interface WorkListProps {
  rows: readonly WorkListRow[];
  /** A shared field order keeps equivalent values aligned across rows. */
  fields?: readonly WorkListField[];
  label?: string;
  title?: string;
  count?: number | null;
  total?: number | null;
  status?: 'ready' | 'loading' | 'checking' | 'failed';
  onRetry?: () => void;
  empty?: { title: string; description?: string; actions?: React.ReactNode };
  selection?: { selected: ReadonlySet<string>; onChange: (selected: Set<string>) => void };
  className?: string;
}

export function WorkList({
  rows,
  fields = [],
  label = 'Records',
  title,
  count,
  total,
  status = 'ready',
  onRetry,
  empty,
  selection,
  className,
}: WorkListProps) {
  return (
    <div className={cn('@container min-w-0', className)}>
      {title && (
        <div className="mb-3 flex min-w-0 items-center gap-2">
          <h2 className="font-sans text-section-title text-text-heading">{title}</h2>
          {count !== undefined && (
            <CountBadge
              count={status === 'ready' ? count : null}
              total={status === 'ready' ? total : undefined}
              label={label}
            />
          )}
        </div>
      )}
      {status === 'loading' ? <StateKit state="loading" /> : null}
      {status === 'checking' ? <StateKit state="checking" /> : null}
      {status === 'failed' ? (
        <StateKit state="failed" onRetry={onRetry ?? (() => window.location.reload())} />
      ) : null}
      {status === 'ready' && rows.length === 0 && empty ? (
        <div>
          <StateKit state="empty" title={empty.title} actions={empty.actions} />
          {empty.description ? (
            <p className="mt-2 text-body text-text-muted">{empty.description}</p>
          ) : null}
        </div>
      ) : null}
      {status === 'ready' && (rows.length > 0 || !empty) ? (
        <ul aria-label={label} className="min-w-0 divide-y divide-border font-body text-body">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex min-w-0 items-start gap-2 py-3"
              {...row.dataAttributes}
              data-work-row
            >
              {selection && (
                <label className="flex min-h-11 shrink-0 items-center">
                  <span className="sr-only">Select {row.label}</span>
                  <input
                    type="checkbox"
                    className="size-4 accent-action"
                    disabled={row.selectionDisabled}
                    checked={selection.selected.has(row.id)}
                    onChange={(event) => {
                      const next = new Set(selection.selected);
                      if (event.target.checked) next.add(row.id);
                      else next.delete(row.id);
                      selection.onChange(next);
                    }}
                  />
                </label>
              )}
              <div
                className={cn(
                  'grid min-w-0 flex-1 grid-cols-1 gap-4',
                  fields.length > 0 ? '@5xl:grid-cols-4' : '@5xl:grid-cols-3',
                )}
              >
                <div className="min-w-0 break-words font-medium text-text-heading [overflow-wrap:anywhere]">
                  {row.identity}
                </div>
                <div className="min-w-0 space-y-1 break-words [overflow-wrap:anywhere]">
                  {row.state !== undefined && (
                    <>
                      <span className="text-caption text-text-muted @5xl:sr-only">Status</span>
                      <div className="min-w-0 text-body font-medium tabular-nums text-text-heading">
                        {row.state}
                      </div>
                    </>
                  )}
                  {row.reason != null && (
                    <div className="min-w-0 break-words text-text-muted [overflow-wrap:anywhere]">
                      {row.reason}
                    </div>
                  )}
                </div>
                {fields.length > 0 && (
                  <dl className="min-w-0 space-y-4">
                    {fields.map((field) => (
                      <div
                        key={field.id}
                        className="min-w-0 space-y-1 break-words [overflow-wrap:anywhere]"
                      >
                        <dt className="text-caption font-normal text-text-muted">{field.label}</dt>
                        <dd className="m-0 min-w-0 text-body font-medium tabular-nums text-text-heading">
                          {row.values?.[field.id] ?? (
                            <EmptyValue
                              label="Not recorded"
                              className="[&>span[aria-hidden]]:hidden [&>.sr-only]:not-sr-only"
                            />
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
                {/* Direct row actions keep a 44px tap target even when callers pass a plain text link. */}
                <div className="flex min-w-0 flex-wrap items-start gap-2 @5xl:justify-end [&>a]:inline-flex [&>a]:min-h-11 [&>a]:items-center [&>button]:min-h-11">
                  {row.action}
                  {row.overflow}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
